import settingsService from '../../services/settingsService';
import { formatDate, mapStatutForDisplay } from '../../services/facturesAPI';

const imageToBase64 = (src, maxWidth, maxHeight) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    try {
      const width = image.naturalWidth || 300;
      const height = image.naturalHeight || 150;
      // Une image de plusieurs milliers de pixels n'apporte rien une fois
      // imprimée sur quelques centimètres. Garder environ 300 dpi maximum.
      const scale = Math.min(1, maxWidth / width, maxHeight / height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      // Le PNG conserve la transparence du logo et du cachet.
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    }
  };
  image.onerror = () => {
    reject(new Error(`Image indisponible: ${src}`));
  };
  image.src = src;
});

const isPaidInvoice = (invoice) => (
  ['payee', 'paid'].includes(String(invoice?.statut || invoice?.status || '').toLowerCase())
);

const invoiceNumber = (invoice) => (
  invoice?.numero || invoice?.numero_facture || 'Non attribu\u00e9'
);

export async function drawModernInvoiceTemplate(doc, invoice) {
  const settings = await settingsService.getInvoiceSettings(invoice);
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;
  const teal = [31, 105, 122];
  const paleTeal = [235, 242, 244];
  const borderTeal = [143, 181, 186];
  const totalIncludingTax = Number.parseFloat(invoice.montant) || 0;
  const subtotal = totalIncludingTax / 1.2;
  const tax = totalIncludingTax - subtotal;
  const money = (value) => `${value.toFixed(2)} DH`;
  const billingDate = formatDate(invoice.dateEmission || invoice.dateCreation || new Date());

  doc.setFillColor(...paleTeal);
  doc.ellipse(pageWidth - 4, -16, 105, 52, 'F');
  doc.ellipse(-20, pageHeight + 15, 90, 36, 'F');

  try {
    if (!settings.logo) throw new Error('Aucun logo configuré');
    const logo = await imageToBase64(settingsService.getLogoUrl(true, settings.logo), 420, 420);
    doc.addImage(logo, 'PNG', margin, 10, 35, 35, undefined, 'SLOW');
  } catch (error) {
    doc.setTextColor(...teal);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text(settings.nom.toUpperCase(), margin, 27);
  }

  const companyX = pageWidth - margin;
  const addressLines = String(settings.adresse || '')
    .split(',')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4);
  doc.setTextColor(20, 32, 48);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(settings.nom.toUpperCase(), companyX, 16, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  addressLines.forEach((line, index) => doc.text(line, companyX, 22 + (index * 5), { align: 'right' }));
  if (settings.telephone) {
    doc.text(`T\u00e9l. ${settings.telephone}`, companyX, 22 + (addressLines.length * 5), { align: 'right' });
  }

  doc.setFontSize(10);
  doc.text(invoice.client || 'Client', margin, 58);
  doc.setTextColor(...teal);
  doc.setFontSize(20);
  doc.text(`Facture ${invoiceNumber(invoice)}`, pageWidth - margin, 76, { align: 'right' });

  doc.setFillColor(249, 251, 251);
  doc.setDrawColor(...borderTeal);
  doc.setLineWidth(0.6);
  doc.roundedRect(margin, 84, pageWidth - (margin * 2), 17, 3, 3, 'FD');
  doc.setTextColor(...borderTeal);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Date de facturation', margin + 3, 91);
  doc.setTextColor(20, 32, 48);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(billingDate, margin + 3, 97);

  const tableY = 108;
  const descriptionX = margin;
  const quantityX = 96;
  const unitX = 119;
  const taxX = 150;
  const amountX = 172;
  const tableRight = pageWidth - margin;
  doc.setFillColor(...teal);
  doc.roundedRect(margin, tableY, tableRight - margin, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('DESCRIPTION', descriptionX + 2, tableY + 6.5);
  doc.text('QT\u00c9', quantityX + 1, tableY + 6.5);
  doc.text('PRIX UNITAIRE', unitX + 1, tableY + 6.5);
  doc.text('TVA', taxX + 2, tableY + 6.5);
  doc.text('MONTANT', tableRight - 2, tableY + 6.5, { align: 'right' });

  const descriptionLines = doc.splitTextToSize(
    invoice.description || 'Service de consultation en immigration',
    quantityX - descriptionX - 5
  );
  const rowHeight = Math.max(14, descriptionLines.length * 5 + 5);
  const rowY = tableY + 10;
  doc.setDrawColor(150, 157, 167);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, tableY, tableRight - margin, 10 + rowHeight, 2, 2, 'S');
  doc.line(margin, rowY, tableRight, rowY);
  [quantityX, unitX, taxX, amountX].forEach((x) => doc.line(x, tableY, x, rowY + rowHeight));
  doc.setTextColor(20, 32, 48);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(descriptionLines, descriptionX + 2, rowY + 6);
  doc.text('1', quantityX + 3, rowY + 6);
  doc.text(money(subtotal), taxX - 3, rowY + 6, { align: 'right' });
  doc.text('20%', amountX - 3, rowY + 6, { align: 'right' });
  doc.text(money(subtotal), tableRight - 2, rowY + 6, { align: 'right' });

  const totalsX = 120;
  const totalsY = rowY + rowHeight;
  const totalsWidth = tableRight - totalsX;
  [
    ['Montant hors taxes', subtotal],
    ['TVA (20%)', tax],
    ['Total', totalIncludingTax]
  ].forEach(([label, value], index) => {
    const y = totalsY + (index * 9);
    const isTotal = index === 2;
    doc.setFillColor(...(isTotal ? teal : [248, 249, 250]));
    doc.rect(totalsX, y, totalsWidth, 9, 'FD');
    doc.setTextColor(...(isTotal ? [255, 255, 255] : [20, 32, 48]));
    doc.setFont('helvetica', isTotal ? 'bold' : 'normal');
    doc.setFontSize(9.5);
    doc.text(label, totalsX + 2, y + 6);
    doc.text(money(value), tableRight - 2, y + 6, { align: 'right' });
  });

  const detailsY = totalsY + 13;
  doc.setTextColor(20, 32, 48);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  if (invoice.methodePaiement) {
    doc.text(`M\u00e9thode de paiement : ${invoice.methodePaiement}`, margin, detailsY);
  }
  doc.text(`Statut de paiement : ${mapStatutForDisplay(invoice.statut)}`, margin, detailsY + 7);

  if (isPaidInvoice(invoice) && settings.cachet) {
    try {
      const stamp = await imageToBase64(settingsService.getImageUrl(settings.cachet), 840, 456);
      const stampProperties = doc.getImageProperties(stamp);
      const maxStampWidth = 70;
      const maxStampHeight = 38;
      const stampRatio = stampProperties.width / stampProperties.height;
      let stampWidth = maxStampWidth;
      let stampHeight = stampWidth / stampRatio;
      if (stampHeight > maxStampHeight) {
        stampHeight = maxStampHeight;
        stampWidth = stampHeight * stampRatio;
      }
      const stampX = pageWidth - margin - stampWidth;
      const stampY = detailsY + 8 + ((maxStampHeight - stampHeight) / 2);
      doc.addImage(stamp, 'PNG', stampX, stampY, stampWidth, stampHeight, undefined, 'SLOW');
    } catch (error) {
      console.warn('Impossible d\u2019ajouter le cachet de facture pay\u00e9e:', error);
    }
  }

  const footerY = pageHeight - 15;
  doc.setDrawColor(20, 32, 48);
  doc.setLineWidth(0.4);
  doc.line(margin, footerY - 7, pageWidth - margin, footerY - 7);
  doc.setTextColor(55, 65, 81);
  doc.setFontSize(7.5);
  doc.text(
    [settings.nom, settings.ice_number && `ICE : ${settings.ice_number}`, settings.if_number && `IF : ${settings.if_number}`, settings.rc_number && `RC : ${settings.rc_number}`].filter(Boolean).join(' | '),
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );
  doc.setTextColor(100, 100, 100);
  doc.text('Page 1 / 1', pageWidth / 2, footerY + 7, { align: 'center' });
  doc.setTextColor(0, 0, 0);
}
